/**
 * The messages query's key and freshness policy, in a leaf module so anything
 * that invalidates the transcript can reach them without pulling in the api
 * client that `chat-messages` (which owns the query itself) imports.
 */

/** Every reader and invalidator of a chat's transcript keys off this. */
export const messagesQueryKey = (chatId: string | undefined) => ['messages', chatId] as const

/** The subset of a react-query `Query` these predicates actually read. */
type FreshnessQuery = { state: { isInvalidated: boolean } }

/**
 * Opening the same chat repeatedly shouldn't re-pull its history — the
 * messages atom already holds it — so the transcript refetches only once
 * something has explicitly invalidated it (see `use-subscribe-to-chat`, which
 * does so when it lets go of a still-live stream).
 *
 * `refetchOnMount` covers only a freshly mounted surface. The always-mounted
 * surfaces keep one long-lived observer that outlives a close and swings its
 * key back to the chat, and react-query routes a key change through
 * `shouldFetchOptionally`, which consults `isStale` alone and never looks at
 * `refetchOnMount`. That path works because `isStaleByTime` short-circuits
 * true on an invalidated query — which is also why `staleTime` belongs to the
 * same policy: it is what that same check falls back to for a chat nothing
 * invalidated, and it is the only thing keeping a key swing from re-pulling
 * every chat it lands on.
 */
export const messagesFreshnessOptions = {
  staleTime: 5 * 60 * 1000,
  refetchOnMount: (query: FreshnessQuery) => (query.state.isInvalidated ? ('always' as const) : false),
}
